import { useEffect, useRef, useState } from "react";
import {
  Outlet,
  useMatches,
  useNavigate,
  useLocation,
  matchRoutes,
} from "react-router-dom";
import { Menu } from "./pages/component/menu/menu";
import { Header } from "./ui";
import { routes } from "./router.jsx";
import { useSelector, useDispatch } from "react-redux";
import { getUser } from "./selectors/commonSelectors";
import { User } from "./features/user";
import {
  openSocialAccountConnectDialog,
  SocialAccountConnect,
} from "./features/social-account-connect";
import { DueInvoicesBanner } from "./components/DueInvoicesBanner";
import { BannerStackProvider } from "./features/banner-stack/banner-precedence";
import { InstagramCampaignBanner } from "./features/instagram-campaign/InstagramCampaignBanner";
import { InstagramCutoverMarkerProvider } from "./features/instagram-campaign/cutover-marker";
import { InstagramPhonePublishingProvider } from "./features/instagram-phone-publishing/phone-publishing-context";
import { SubscriptionBanner } from "./components/SubscriptionBanner/subscription-banner";
import {
  ServiceStatusBanner,
  ServiceStatusProvider,
} from "./features/service-status";
import { showModalsIfNeeded } from "./libs/modals/init";
import { checkAndShowDraftRestoration } from "./features/post/bulk-create/draft-restoration";
import { useTosStatusCheck } from "./hooks/useTosStatusCheck";
import { TosAcceptanceDialog } from "./components/TosAcceptanceDialog";
import { useAccountsNeedingReconnection } from "./hooks/useAccountsNeedingReconnection";
import { AccountReconnectionDialog } from "./components/AccountReconnectionDialog";
import ModalRoot from "./components/ModalRoot/ModalRoot";
import { cn } from "./lib/utils";
import { ConnectAnotherWayPrompt } from "./features/social-account-connect/components/connect-another-way-prompt/connect-another-way-prompt";
/**
 * Extract GA4 client_id from the _ga cookie.
 * Inlined here to avoid importing analytics-init.ts (which eagerly pulls in
 * the full @amplitude SDK). The canonical copy lives in analytics-init.ts.
 */
function getGaClientId(): string | undefined {
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(.+?)(?:;|$)/);
  return match?.[1];
}
import { resetSessionState } from "./api/_tokenRefresh";
import { logout } from "./utils/accounts";
import {
  buildPostsListRoute,
  getConsumablePostsDeepLinkPostId,
  isPostsShellPath,
} from "./components/Posts/posts-deep-link";
import { PAGES } from "./constants/Pages";
import { isPaidSocialInsightsPreviewPath } from "./pages/insights-preview/insights-preview-paths";
import { useSingleScrollerCheck } from "./utils/hooks/useSingleScrollerCheck";

export default function RootComponent(): JSX.Element {
  const match = useMatches();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const contentPanelRef = useRef<HTMLDivElement>(null);
  // Logs a console error off production when a route scrolls twice.
  useSingleScrollerCheck(contentPanelRef, location.pathname);

  const user: User = useSelector(getUser);
  const currentUserId = user.userId;

  // Check TOS status and show modal if needed
  const { showTosModal, onTosAccepted } = useTosStatusCheck(user);

  // Check if any accounts need reconnection
  const {
    accountsNeedingReconnection,
    hasAccountsNeedingReconnection,
    isDismissed,
  } = useAccountsNeedingReconnection();

  // State to track if user has dismissed the reconnection dialog this session
  const [reconnectDialogDismissed, setReconnectDialogDismissed] =
    useState(false);

  // Check if bulk create modal is being auto-opened
  const params = new URLSearchParams(window.location.search);
  const isAutoOpeningBulkCreate = params.get("autoOpenBulkCreate") === "true";
  const isAdminRoute = /^\/admin(?:-v2)?(?:\/|$)/.test(
    window.location.pathname
  );

  // Show reconnection dialog if:
  // 1. There are accounts needing reconnection
  // 2. User hasn't dismissed it for a week (localStorage)
  // 3. User hasn't dismissed it this session
  // 4. TOS modal is not showing (TOS takes priority)
  // 5. Bulk create modal is not being auto-opened
  const showReconnectDialog =
    hasAccountsNeedingReconnection &&
    !isDismissed &&
    !reconnectDialogDismissed &&
    !showTosModal &&
    !isAutoOpeningBulkCreate &&
    !isAdminRoute; // Admin users won't see the reconnection dialog since they don't manage social accounts

  // FontAwesome kit is loaded via <script defer> in the shared app shell HTML to
  // start the download earlier (in parallel with JS bundles). The old dynamic
  // injection was removed to eliminate the late-loading FOUC.

  useEffect(() => {
    /** match.length === 1 means matching root level / */
    if (currentUserId && match.length === 1) {
      /** Navigate immediately to dashboard on root path */
      navigate(`/dashboard${window.location.search}`);
    }
  }, [currentUserId, match.length, navigate]);

  // Notification links can land on any dashboard URL. Resolve valid post
  // IDs through the modern Posts shell while leaving create-post sentinels
  // and malformed values to their existing route behavior.
  useEffect(() => {
    if (
      !currentUserId ||
      !location.pathname.startsWith("/dashboard") ||
      isPostsShellPath(location.pathname)
    ) {
      return;
    }

    if (!getConsumablePostsDeepLinkPostId(location.search)) return;

    navigate(buildPostsListRoute(PAGES.UPCOMING, location.search), {
      replace: true,
    });
  }, [currentUserId, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (currentUserId) {
      showModalsIfNeeded();
      checkAndShowDraftRestoration(dispatch, currentUserId);
    }
  }, [dispatch, currentUserId]);

  // Fire once per Root mount: wait until `match.length > 1` so the
  // root-redirect effect has flushed (otherwise Page Path captures as `/`),
  // and the ref keeps it from re-firing as the user navigates between
  // `/dashboard/*` sub-routes (which change `match.length`).
  const dashboardLoadedFiredRef = useRef(false);
  useEffect(() => {
    if (
      !currentUserId ||
      match.length === 1 ||
      dashboardLoadedFiredRef.current
    ) {
      return;
    }
    dashboardLoadedFiredRef.current = true;
    const gaClientId = getGaClientId();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dashboard_loaded",
      user_id: currentUserId,
      ...(gaClientId ? { user_pseudo_id: gaClientId } : {}),
    });
  }, [currentUserId, match.length]);

  // Reset the auth session-invalid flag on navigation so retries are possible
  // after the user logs back in via another tab.
  useEffect(() => {
    resetSessionState();
  }, [location.pathname]);

  // Resolve metadata for the same location as the rendered Outlet. Reading the
  // imported router's mutable state can pair a new page with a stale header.
  const pageRoute = matchRoutes(routes, location)?.[1]?.route as any;
  const ncyBreadcrumb = pageRoute?.ncyBreadcrumb;
  const headerVariant = pageRoute?.headerVariant;
  const resolvedHeaderVariant = isPostsShellPath(location.pathname)
    ? "posts"
    : isPaidSocialInsightsPreviewPath(location.pathname)
      ? "paid-social"
      : headerVariant;

  // Support dynamic breadcrumbs via getLabel/getSubLabel functions
  const pageTitle =
    ncyBreadcrumb?.getLabel?.(location.pathname) ?? ncyBreadcrumb?.label;
  const subTitle =
    ncyBreadcrumb?.getSubLabel?.(location.pathname) ?? ncyBreadcrumb?.subLabel;

  return (
    // One shared SSE connection to status.skedsocial.com for the whole shell:
    // the banner below and the header dot both read from this provider.
    <ServiceStatusProvider>
      <div className="flex w-screen h-screen overflow-hidden bg-blue-37">
        <Menu />
        {/*
          THIS PANEL IS THE PAGE SCROLLER. The header and any banner sit in it
          ABOVE the route, so a route root with a 100% or viewport height
          (h-full, min-h-full, h-screen, min-h-screen) overflows by their
          height and the page scrolls twice. A route root takes no height and
          no overflow. To fill a short page, use a :has() flex override in
          shadcn.css (see .collect-route-shell), never a percentage floor.
          Off production, useSingleScrollerCheck logs a console error for it.
        */}
        <div
          ref={contentPanelRef}
          className={cn(
            "dashboard-content-panel mt-2 mr-2 mb-2 min-w-0 flex-1 rounded-xl bg-blue-25 shadow-[var(--shadow-panel-left)]",
            isPostsShellPath(location.pathname)
              ? "ml-4 h-[calc(100vh-16px)] min-h-0 overflow-hidden"
              : isPaidSocialInsightsPreviewPath(location.pathname)
                ? "h-[calc(100dvh-16px)] min-h-0 overflow-hidden"
                : "min-h-screen overflow-y-auto",
            headerVariant === "calendar-uplift" && "ml-4"
          )}
        >
          <Header
            variant={resolvedHeaderVariant}
            pageTitle={pageTitle}
            subTitle={subTitle}
            logout={logout}
            customLinkInTitle={ncyBreadcrumb?.customLinkInTitle}
            openSocialAccountConnectDialog={openSocialAccountConnectDialog}
          />
          {/*
            SKE-4701. DOM order still reads as the priority order, but the
            order is no longer what enforces it: every banner here is
            independently `sticky top-0 z-50`, so two that wanted the slot at
            once used to overlap. `BannerStackProvider` ranks them and each one
            asks before rendering. Incident outranks billing, billing outranks
            the campaign.

            SKE-4881. This panel is the page scroller, so a banner pinned to its
            top stays over the page chrome that is sticky underneath it, and the
            Insights toolbar at `top: 16px` lost its account picker, date range
            and Create report to it. The banners stay sticky; the chrome beneath
            moves down instead. Each banner publishes its measured height as
            `--banner-height` (`features/banner-stack/banner-offset.ts`) and the
            sticky surfaces below add it to their own offset. No banner mounted
            means no property, which is exactly the layout that shipped.
          */}
          <BannerStackProvider>
            <ServiceStatusBanner />
            <DueInvoicesBanner />
            <SubscriptionBanner />
            <InstagramCampaignBanner />
          </BannerStackProvider>
          {/*
            SKE-4686. The campaign status is read ONCE here and handed to the
            post surfaces through context, rather than each post card opening
            its own query. A month view renders hundreds of cards, and
            `useQuery` throws outright in any tree with no QueryClientProvider
            above it — which is how the card components are unit-tested.
          */}
          <InstagramCutoverMarkerProvider>
            <InstagramPhonePublishingProvider>
              <Outlet />
            </InstagramPhonePublishingProvider>
          </InstagramCutoverMarkerProvider>
        </div>
        {/* NOTE: move SocialAccountConnect dialog into the root dialog stack when that UI flow is rewritten */}
        <SocialAccountConnect />
        <ConnectAnotherWayPrompt />
      </div>

      {/* TOS Acceptance Modal - shown when user needs to accept updated Terms of Service */}
      <TosAcceptanceDialog open={showTosModal} onAccepted={onTosAccepted} />

      {/* Account Reconnection Modal - shown when accounts need reconnection */}
      <AccountReconnectionDialog
        open={showReconnectDialog}
        accountsNeedingReconnection={accountsNeedingReconnection}
        onDismiss={() => setReconnectDialogDismissed(true)}
      />

      {/* Global modal root for redux-managed modals (bulk-create, media picker, etc.) */}
      <ModalRoot />
    </ServiceStatusProvider>
  );
}
